import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, SelectProps, Spin, Select } from 'antd'
import { useMemo, useState } from 'react'

import { useStocksServiceGetGroupListEndpointKey } from 'src/api/queries'
import {
  GetGroupListResponse,
  ProductGroupCategory,
  StocksService
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { useDebounce, useDefaultSortingItem } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  id?: string
  hasDefault?: boolean
  groupCategory?: ProductGroupCategory
} & SelectProps

export const GroupSelect = ({
  id,
  hasDefault,
  placeholder,
  groupCategory,
  ...rest
}: Props) => {
  const { t, currentLang } = useLocales()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)

  const { defaultSortingOption } = useDefaultSortingItem()

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useStocksServiceGetGroupListEndpointKey,
      String(id),
      debouncedSearch
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getGroupListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        debouncedSearch,
        groupCategory
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetGroupListResponse[] | undefined
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1,
    enabled: !!id
  })

  const selectOptions = useMemo(
    () => [
      ...(hasDefault && defaultSortingOption ? [defaultSortingOption] : []),
      ...(data?.pages.flatMap(
        (page) =>
          page.data?.map((item: GetGroupListResponse) => ({
            value: item?.id,
            label: item?.name?.[currentLang?.value]
          })) || []
      ) || [])
    ],
    [hasDefault, data, currentLang]
  )

  return (
    <Select
      allowClear
      popupMatchSelectWidth={true}
      filterOption={false}
      options={selectOptions}
      onSearch={(value) => setSearch(value)}
      onClear={() => setSearch('')}
      className="full-width"
      loading={isFetching}
      placeholder={placeholder ?? t('common.group')}
      showSearch={true}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight)
          fetchNextPage()
      }}
      notFoundContent={
        isFetching ? (
          <Flex justify="center">
            <Spin spinning />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...rest}
    />
  )
}
